import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';

/**
 * Values the selection can hold that mean "no company": the model's default, and the literal strings
 * localStorage ends up with when the account flow writes null or undefined into it.
 */
const NO_COMPANY = ['some_company_id', 'null', 'undefined'];

/** What the saved company name falls back to when there is none. */
const NO_COMPANY_NAME = 'Select account';
const usable = id => id && !NO_COMPANY.includes(id) ? id : null;

/**
 * The company the Bills page is showing: the live selection, or localStorage before the store has one.
 *
 * The live selection comes first because it moves the moment a company is picked, while localStorage is
 * written only when the company fetch that follows the switch comes back -- and nothing observes it, so a
 * reader keyed on it neither re-renders on a switch nor knows it is behind. Every part of the page that
 * acts on "this company" has to answer the same way: the settings modal used to read localStorage while
 * the banner read the selection, so for a moment after a switch the toolbar's settings could show, and
 * rotate, a different company's address from the one on the banner above it (LB-5903).
 *
 * Read it during render of an observer, so a switch re-renders the reader.
 */
export const billsPageCompanyId = store => usable(store.homeDashboardStore.companyAccessId) || usable(localStorage.getItem(USER_SETTINGS_KEYS.company));

/**
 * The name of `companyId`, or undefined when the page cannot vouch for it.
 *
 * The dashboard's company list is the first place to look, but it is filled by the dashboard's own fetch,
 * so it is empty when the Bills page is opened directly. Then the name saved alongside the company in
 * localStorage is used -- only when that entry is for this company, because the pair is written when the
 * company fetch after a switch comes back, and until then it still names the previous one. No name is
 * better than the wrong one on a screen that is about to rotate an address.
 */
export const billsPageCompanyName = (store, companyId) => {
  var _store$homeDashboardS;
  if (!companyId) return undefined;
  const listed = (_store$homeDashboardS = store.homeDashboardStore.getCompanyById(companyId)) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.name;
  if (listed) return listed;
  const savedName = localStorage.getItem(USER_SETTINGS_KEYS.companyName);
  const savedFor = localStorage.getItem(USER_SETTINGS_KEYS.company);
  return savedFor === companyId && savedName && savedName !== NO_COMPANY_NAME ? savedName : undefined;
};